import { useCallback, useEffect, useRef, useState } from 'react';

const tabId = crypto.randomUUID();

const MESSAGE_TYPES = {
  NEW_LAST_ACTIVE_TAB: 'NEW_LAST_ACTIVE_TAB',
  LAST_ACTIVE_TAB_CLOSED: 'LAST_ACTIVE_TAB_CLOSED',
} as const;

type Messages = {
  type: (typeof MESSAGE_TYPES)[keyof typeof MESSAGE_TYPES];
};

export type UseIdleProps = {
  /**
   * The name of the channel to use for communication between the tabs.
   * Providing it will enable cross-tab communication.
   * NOTE: This should be unique for each instance of the hook.
   * NOTE: Changing it will not remove or enable cross-tab communication.
   */
  channelName?: string;
  /**
   * Whether the last active tab should stay active.
   */
  lastActiveTabToStayActive?: boolean;
};

const useIdle = ({ channelName, lastActiveTabToStayActive }: UseIdleProps) => {
  const [isIdle, setIsIdle] = useState(false);
  const channel = useRef(channelName ? new BroadcastChannel(channelName) : undefined);

  const sendMessage = useCallback(
    (message: Messages) => {
      if (!channelName) {
        return;
      }

      channel.current!.postMessage({
        ...message,
        tabId,
      });
    },
    [channelName],
  );

  const setAsLastActiveTab = useCallback(() => {
    sendMessage({
      type: MESSAGE_TYPES.NEW_LAST_ACTIVE_TAB,
    });

    setIsIdle(false);
  }, [sendMessage]);

  const onMessage = useCallback(
    (event: MessageEvent) => {
      const message = event.data as Messages & { tabId: string };

      switch (message.type) {
        case MESSAGE_TYPES.NEW_LAST_ACTIVE_TAB:
          setIsIdle(true);
          break;
        case MESSAGE_TYPES.LAST_ACTIVE_TAB_CLOSED:
          setAsLastActiveTab();
          break;
        default:
          break;
      }
    },
    [setAsLastActiveTab],
  );

  useEffect(() => {
    const currentChannel = channel.current;
    currentChannel?.addEventListener('message', onMessage);
    return () => {
      currentChannel?.removeEventListener('message', onMessage);
    };
  }, [onMessage]);

  useEffect(() => {
    const onVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        if (lastActiveTabToStayActive) {
          return;
        }

        sendMessage({
          type: MESSAGE_TYPES.LAST_ACTIVE_TAB_CLOSED,
        });
        setIsIdle(true);
      } else {
        sendMessage({
          type: MESSAGE_TYPES.NEW_LAST_ACTIVE_TAB,
        });
        setIsIdle(false);
      }
    };

    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [lastActiveTabToStayActive, sendMessage]);

  useEffect(() => {
    const handleBeforeUnload = () => {
      sendMessage({
        type: MESSAGE_TYPES.LAST_ACTIVE_TAB_CLOSED,
      });
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [sendMessage]);

  useEffect(() => {
    setAsLastActiveTab();
  }, [setAsLastActiveTab]);

  return { channelName, tabId, isIdle };
};

export { useIdle };
